import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import '@/styles/Navigation/SideBar.less';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@/assets/icons/LibrarySvg';
import SwitchToggle from '@/components/UI/SwitchToggle';
import { useApiContext } from '@/context/ApiContext';
import { useAuth } from '@/context/AuthContext';
import { UserGroupsType } from '@/types';
import { initTheme, setTheme } from '@/utils/theme';
const SideBar = memo(() => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const sidebarRef = useRef(null);
    const { user, loading: isAuthLoading, logout } = useAuth();
    const { t, i18n } = useTranslation();
    //
    //
    const [theme, setLocalTheme] = useState(() => {
        try {
            return (localStorage.getItem('app_theme') ||
                (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
                    ? 'dark'
                    : 'light'));
        }
        catch (_a) {
            return 'light';
        }
    });
    useEffect(() => {
        // гарантируем, что состояние соответствует реальному классу
        initTheme();
    }, []);
    const toggle = () => {
        const next = theme === 'dark' ? 'light' : 'dark';
        setTheme(next);
        setLocalTheme(next);
    };
    //
    //
    useEffect(() => {
        if (isAuthLoading)
            return;
    }, [isAuthLoading]);
    // --- useMockApi с localStorage ---
    const { useMockApi, setUseMockApi } = useApiContext();
    useEffect(() => {
        const stored = localStorage.getItem('useMockApi');
        if (stored !== null) {
            setUseMockApi(stored === 'true');
        }
    }, [setUseMockApi]);
    const handleMockApiChange = (checked) => {
        localStorage.setItem('useMockApi', checked ? 'true' : 'false');
        setUseMockApi(checked);
        window.location.reload();
    };
    // Пример переключателя языка
    const handleLangChange = (lang) => {
        i18n.changeLanguage(lang);
        localStorage.setItem('i18nextLng', lang);
    };
    // Закрытие по ESC
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape')
                setIsOpen(false);
        };
        if (isOpen)
            window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);
    // Закрытие по клику вне сайдбара
    useEffect(() => {
        const handleClick = (e) => {
            if (isOpen && sidebarRef.current && !sidebarRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen)
            document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isOpen]);
    const getStyleForLink = (path) => {
        return { fontWeight: location.pathname === path ? 'bold' : 'normal' };
    };
    const handleLogout = () => {
        logout();
        navigate('/auth', { replace: true });
        setIsOpen(false);
    };
    return (_jsxs(_Fragment, { children: [_jsx("button", { "aria-label": "Open navigation", className: "nav-menu-hamburger-button", onClick: () => setIsOpen(true), children: _jsx(Icon, { name: "hamburger" }) }), _jsxs("div", { className: "nav-menu-side-bar", ref: sidebarRef, style: { transform: isOpen ? 'translateX(0)' : 'translateX(-100%)' }, tabIndex: -1, children: [_jsx("div", { className: "nav-menu-title", children: _jsx("span", { children: t('sidebar.title') }) }), _jsxs("nav", { className: "nav-menu-links-container", children: [_jsx(Link, { className: "nav-link", to: "/auth", onClick: () => setIsOpen(false), style: getStyleForLink('/auth'), children: t('sidebar.home') }), user && !useMockApi ? (_jsxs(_Fragment, { children: [_jsx(Link, { className: "nav-link", to: "/profile", onClick: () => setIsOpen(false), style: getStyleForLink('/profile'), children: t('sidebar.profile') }), _jsx(Link, { className: "nav-link", to: "/", onClick: () => handleLogout(), children: t('sidebar.logout') })] })) : (_jsx(Link, { className: "nav-link", to: "/auth", onClick: () => setIsOpen(false), style: getStyleForLink('/auth'), children: t('sidebar.auth') })), _jsx("br", {}), _jsx("hr", {}), _jsx("span", { className: "sub-title", children: t('sidebar.knowledgeBase') }), _jsx(Link, { className: "nav-link sub-link", to: "/spells", onClick: () => setIsOpen(false), style: getStyleForLink('/spells'), children: t('sidebar.spells') }), _jsx("br", {}), _jsx("hr", {}), _jsx("span", { className: "sub-title", children: t('sidebar.InitiativeTracker', 'Трекер инициативы') }), _jsx(Link, { className: "nav-link sub-link", to: "/characters", onClick: () => setIsOpen(false), style: getStyleForLink('/characters'), children: t('sidebar.myCharacters') }), _jsx(Link, { className: "nav-link sub-link", to: "/combat-maps", onClick: () => setIsOpen(false), style: getStyleForLink('/combat-maps'), children: t('sidebar.prepareCombat') }), _jsx(Link, { className: "nav-link sub-link", to: "/initiative-tracker", onClick: () => setIsOpen(false), style: getStyleForLink('/initiative-tracker'), children: t('sidebar.initiativeTracker') }), _jsx(Link, { className: "nav-link sub-link", to: "/rooms", onClick: () => setIsOpen(false), style: getStyleForLink('/rooms'), children: t('sidebar.rooms') }), user && user.groups.includes(UserGroupsType.BOARD_ACCESS) && (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx("hr", {}), _jsx("span", { className: "sub-title", children: t('sidebar.InitiativeTracker', 'Интерактивные доски') }), _jsx(Link, { className: "nav-link sub-link", to: "/boards", onClick: () => setIsOpen(false), style: getStyleForLink('/boards'), children: t('sidebar.boards', 'Подключиться / Создать доску') })] }))] }), _jsxs("div", { className: "theme-select theme-toggle", children: [_jsx("span", { children: t('user.userSettings.theme.title') }), _jsx(SwitchToggle, { checked: theme === 'dark', onChange: toggle, labelLeft: '☀️', labelRight: '🌙' })] }), _jsxs("div", { className: "footer", children: [_jsxs("div", { className: "change-lang-container", children: [_jsx("button", { className: "white-btn", onClick: () => handleLangChange('ru'), children: "ru" }), _jsx("button", { className: "white-btn", onClick: () => handleLangChange('en'), children: "en" })] }), _jsx("br", {}), _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: useMockApi, onChange: (e) => handleMockApiChange(e.target.checked), style: { marginRight: 4 } }), t('sidebar.useMockApi')] }), _jsx("span", { children: t('sidebar.version', { version: '0.1.0' }) }), _jsx("span", { className: "beta-version-notice", children: "Beta version" })] })] }), _jsx("div", { className: `nav-menu-overlay ${isOpen ? 'show' : ''}`, onClick: () => setIsOpen(false) })] }));
});
SideBar.displayName = 'SideBar';
export default SideBar;
